import React, { useEffect } from 'react'
import { FormattedMessage } from 'react-intl'
import { Card, CardContent, Typography } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import { CapeeshColors } from '../../assets/ColorPalette'

const PROVIDER_CONFIG = {
    Vipps: {
        titleId: 'auth.identity.verify.vipps.title',
        titleDefault: 'Verify with Vipps',
        descriptionId: 'auth.identity.verify.vipps.description',
        descriptionDefault: 'Open the Vipps app to confirm your identity',
    },
    BankID: {
        titleId: 'auth.identity.verify.bankid.title',
        titleDefault: 'Verify with BankID',
        descriptionId: 'auth.identity.verify.bankid.description',
        descriptionDefault: 'Use BankID on mobile or code device',
    },
}

const useStyles = makeStyles((theme) => ({
    root: {
        padding: theme.spacing(3),
        maxWidth: 480,
        margin: '0 auto',
    },
    header: {
        marginBottom: theme.spacing(3),
        textAlign: 'center',
    },
    title: {
        fontSize: 24,
        fontWeight: 600,
        color: CapeeshColors.DarkText,
        marginBottom: theme.spacing(1),
    },
    subtitle: {
        fontSize: 14,
        color: CapeeshColors.Gray1,
    },
    cardsContainer: {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(2),
    },
    providerCard: {
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        border: `2px solid ${CapeeshColors.Gray4}`,
        '&:hover': {
            borderColor: CapeeshColors.Primary400,
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
        },
    },
    providerCardPreferred: {
        borderColor: CapeeshColors.Primary400,
        backgroundColor: CapeeshColors.Primary100,
    },
    cardContent: {
        padding: theme.spacing(3),
        '&:last-child': {
            paddingBottom: theme.spacing(3),
        },
    },
    lastUsedLabel: {
        display: 'inline-block',
        fontSize: 11,
        fontWeight: 600,
        color: CapeeshColors.Primary600,
        textTransform: 'uppercase',
        letterSpacing: 0.5,
        marginBottom: theme.spacing(1),
    },
    cardTitle: {
        fontSize: 18,
        fontWeight: 600,
        color: CapeeshColors.DarkText,
        marginBottom: theme.spacing(0.5),
    },
    cardDescription: {
        fontSize: 14,
        color: CapeeshColors.Gray1,
    },
}))

const IdentityProviderSelector = ({
    allowedProviders = [],
    preferredProvider = null,
    partialAuthToken,
    onProviderSelected,
}) => {
    const classes = useStyles()

    useEffect(() => {
        if (allowedProviders.length === 1 && onProviderSelected) {
            onProviderSelected(allowedProviders[0])
        }
    }, [allowedProviders, onProviderSelected])

    if (allowedProviders.length === 0) {
        return null
    }

    if (allowedProviders.length === 1) {
        return null
    }

    const handleCardClick = (providerName) => {
        if (onProviderSelected) {
            onProviderSelected(providerName)
        }
    }

    return (
        <div className={classes.root}>
            <div className={classes.header}>
                <Typography component="h1" className={classes.title}>
                    <FormattedMessage
                        id="auth.identity.verify.header"
                        defaultMessage="Verify your identity"
                    />
                </Typography>
                <Typography component="p" className={classes.subtitle}>
                    <FormattedMessage
                        id="auth.identity.verify.subtitle"
                        defaultMessage="Your organization requires identity verification to continue"
                    />
                </Typography>
            </div>

            <div className={classes.cardsContainer}>
                {allowedProviders.map((providerName) => {
                    const config = PROVIDER_CONFIG[providerName]
                    if (!config) return null

                    const isPreferred = preferredProvider === providerName

                    return (
                        <Card
                            key={providerName}
                            className={`${classes.providerCard} ${isPreferred ? classes.providerCardPreferred : ''}`}
                            onClick={() => handleCardClick(providerName)}
                            elevation={0}
                        >
                            <CardContent className={classes.cardContent}>
                                {isPreferred && (
                                    <Typography className={classes.lastUsedLabel}>
                                        <FormattedMessage
                                            id="auth.identity.verify.last.used"
                                            defaultMessage="Last used"
                                        />
                                    </Typography>
                                )}
                                <Typography className={classes.cardTitle}>
                                    <FormattedMessage
                                        id={config.titleId}
                                        defaultMessage={config.titleDefault}
                                    />
                                </Typography>
                                <Typography className={classes.cardDescription}>
                                    <FormattedMessage
                                        id={config.descriptionId}
                                        defaultMessage={config.descriptionDefault}
                                    />
                                </Typography>
                            </CardContent>
                        </Card>
                    )
                })}
            </div>
        </div>
    )
}

IdentityProviderSelector.propTypes = {}

export default IdentityProviderSelector
